/**
 * Generic CSV utilities for parsing and processing CSV files
 * Contains reusable functions that can be used across different components
 */

const SEPARATORS = [',', ';', '\t', '|']

// How each country writes numbers in a spreadsheet, and the column separator its Excel expects. A
// country missing here has no declared format, so its CSV tools are not offered.
export const CSV_FORMATS = {
  BR: { decimal: ',', delimiter: ';' },
  MX: { decimal: '.', delimiter: ',' },
}

/**
 * Detects the most likely separator used in a CSV text by analyzing the first line
 * @param {string} csvText - The CSV text content
 * @returns {string} The detected separator character
 */
export const detectSeparator = (csvText) => {
  const firstLine = csvText.split('\n')[0]
  
  let maxColumns = 0
  let detectedSeparator = ','
  
  SEPARATORS.forEach(separator => {
    const columns = firstLine.split(separator)
    if (columns.length > maxColumns) {
      maxColumns = columns.length
      detectedSeparator = separator
    }
  })
  
  return detectedSeparator
}

/**
 * Normalizes column name by trimming and converting to lowercase
 * @param {string} columnName - Column name to normalize
 * @returns {string} Normalized column name
 */
export const normalizeColumnName = (columnName) => {
  return columnName.trim().toLowerCase()
}

/**
 * Finds a column by name (case insensitive) in headers array
 * @param {string[]} headers - Array of header names
 * @param {string} targetColumnName - Column name to find
 * @returns {string|null} Found column name or null
 */
export const findColumnByName = (headers, targetColumnName) => {
  const normalizedTarget = normalizeColumnName(targetColumnName)
  
  for (let i = 0; i < headers.length; i++) {
    if (normalizeColumnName(headers[i]) === normalizedTarget) {
      return headers[i]
    }
  }
  
  return null
}

/**
 * Parses a single CSV line handling quoted fields and separators
 * Supports fields with quotes and separators inside quoted content
 * @param {string} line - The CSV line to parse
 * @param {string} separator - The separator character to use
 * @returns {string[]} Array of parsed field values
 */
export const parseCsvLine = (line, separator) => {
  const result = []
  let current = ''
  let inQuotes = false
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === separator && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  
  result.push(current.trim())
  return result
}

/**
 * Splits CSV text into lines and removes empty lines
 * @param {string} csvText - The CSV text content
 * @returns {string[]} Array of non-empty lines
 */
export const splitCsvLines = (csvText) => {
  return csvText.split('\n').filter(line => line.trim())
}

/**
 * Parses CSV headers from the first line
 * @param {string} csvText - The CSV text content
 * @returns {string[]} Array of header names
 */
export const parseCsvHeaders = (csvText) => {
  const lines = splitCsvLines(csvText)
  if (lines.length === 0) return []
  
  const separator = detectSeparator(csvText)
  return parseCsvLine(lines[0], separator)
}

/**
 * Creates a downloadable CSV file with the given content
 * @param {string} content - CSV content as string
 * @param {string} filename - Filename for download
 */
export const downloadCsv = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

/**
 * Generates and downloads a basic CSV template file
 * @param {string} [filename='template.csv'] - The filename for the download
 * @param {string[]} headers - Array of header names
 * @param {string[][]} [sampleData=[]] - Array of sample rows
 */
export const generateCsvTemplate = (filename = 'template.csv', headers = [], sampleData = []) => {
  let content = headers.join(',')
  
  if (sampleData.length > 0) {
    content += '\n' + sampleData.map(row => row.join(',')).join('\n')
  }
  
  downloadCsv(content, filename)
} 